import React, { useEffect, useState } from "react";
import { Row, Col, Button, Container, Card } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";
import {
  getBankingDetailsByPracticeNumbers,
  decryptField,
} from "../../afinanceapi";
import { FaEye, FaEyeSlash, FaEdit } from "react-icons/fa";

import EditBankingModal from "./EditBankingPayments";
import {
  YocoStatusBadge,
  fetchYocoState,
  fetchYocoDevices,
  YOCO_STATE,
} from "./YocoConnect";

export function BankingPayments({ refresh, onBankingUpdate = () => {} }) {
  const [bankingData, setBankingData] = useState([]);
  const [visibleFields, setVisibleFields] = useState({}); // track shown fields
  const [practices, setPractices] = useState([]);
  const [selectedBanking, setSelectedBanking] = useState(null); // track selected banking for editing
  const [modalShow, setModalShow] = useState(false); // track modal visibility
  // { [practiceNumber]: "CONNECTED" | "NEEDS_RECONNECT" | "NOT_CONNECTED" | "UNKNOWN" }
  const [yocoStatuses, setYocoStatuses] = useState({});
  const [yocoDevices, setYocoDevices] = useState({}); // { [practiceNumber]: Device[] }
  const [checkingYoco, setCheckingYoco] = useState(false);

  useEffect(() => {
    async function loadData() {
      const token = sessionStorage.getItem("user");
      if (!token) return;

      const decoded = jwtDecode(token);
      const userPractices = await getUserPracticeById(decoded._id);
      setPractices(userPractices);

      const practiceNumbers = userPractices.map((p) => p.practiceNumber);
      const result = await getBankingDetailsByPracticeNumbers(practiceNumbers);
      setBankingData(result);

      // Status is keyed off the user's practices, not their banking records, so
      // a practice with no banking details still reports a Yoco state. Each
      // lookup resolves independently — the previous Promise.all meant one
      // failure left every badge stuck on "Status: …".
      setCheckingYoco(true);
      const entries = await Promise.all(
        practiceNumbers.map(async (pn) => [
          pn,
          await fetchYocoState(pn),
          await fetchYocoDevices(pn),
        ])
      );
      setYocoStatuses(Object.fromEntries(entries.map(([pn, state]) => [pn, state])));
      setYocoDevices(Object.fromEntries(entries.map(([pn, , devices]) => [pn, devices])));
      setCheckingYoco(false);
    }

    loadData();
  }, [refresh]);

  const toggleVisibility = async (fieldKey, encryptedValue) => {
    if (visibleFields[fieldKey]) {
      setVisibleFields((prev) => ({ ...prev, [fieldKey]: null }));
    } else {
      const decrypted = await decryptField(encryptedValue);
      setVisibleFields((prev) => ({ ...prev, [fieldKey]: decrypted }));
    }
  };

  const renderSecureField = (fieldKey, encryptedValue) => (
    <span>
      {visibleFields[fieldKey] || "*****"}
      <Button
        variant="link"
        size="sm"
        className="ms-2 p-0"
        onClick={() => toggleVisibility(fieldKey, encryptedValue)}
      >
        {visibleFields[fieldKey] ? <FaEyeSlash /> : <FaEye />}
      </Button>
    </span>
  );

  const handleEditClick = (entry) => {
    setSelectedBanking(entry);
    setModalShow(true);
  };

  return (
    <Container fluid className="pt-3">
      {bankingData.map((entry, idx) => {
        const practice = practices.find(
          (p) => p.practiceNumber === entry.practiceNumber
        );
        const title = practice ? practice.practiceName : entry.practiceNumber;

        return (
          <Card key={idx} className="mb-4 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center" style={{background: "#CFE6FF"}}>

              <div className="d-flex align-items-center gap-3">
                <h6 className="mb-0 fw-bold text-primary">{title}</h6>
                <small>
                  <YocoStatusBadge
                    state={yocoStatuses[entry.practiceNumber]}
                    loading={checkingYoco}
                  />
                </small>
              </div>
              <div className="d-flex align-items-center gap-2">
                {/* Connecting Yoco lives in the practice's banking editor with
                    the rest of its payment configuration. Starting OAuth from a
                    summary row gave a popup with nothing to return to. */}
                {yocoStatuses[entry.practiceNumber] === YOCO_STATE.NEEDS_RECONNECT && (
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => handleEditClick(entry)}
                  >
                    Reconnect Yoco
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => handleEditClick(entry)}
                  title="Edit"
                >
                  <FaEdit />
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              <Row>
                <Col md={4}>
                  <h6 className="text-primary mb-3">Bank Details</h6>
                  {/* Bank Details fields */}
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Bank Name:
                    </Col>
                    <Col xs={7}>{entry.bank.bankName}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Branch Code:
                    </Col>
                    <Col xs={7}>{entry.bank.branchCode}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Account Number:
                    </Col>
                    <Col xs={7}>
                      {renderSecureField(
                        `${idx}-accountNumber`,
                        entry.bank.accountNumber
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Account Name:
                    </Col>
                    <Col xs={7}>
                      {renderSecureField(
                        `${idx}-accountName`,
                        entry.bank.accountName
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Account Type:
                    </Col>
                    <Col xs={7}>{entry.bank.accountType}</Col>
                  </Row>
                </Col>

                <Col md={4}>
                  <h6 className="text-primary mb-3">Ozow</h6>
                  {/* Ozow fields */}
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Merchant Code:
                    </Col>
                    <Col xs={7}>{entry.ozow.merchantCode}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      API Key:
                    </Col>
                    <Col xs={7}>
                      {renderSecureField(
                        `${idx}-ozowApiKey`,
                        entry.ozow.apiKey
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Private Key:
                    </Col>
                    <Col xs={7}>
                      {renderSecureField(
                        `${idx}-ozowPrivateKey`,
                        entry.ozow.privateKey
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Payment Link:
                    </Col>
                    <Col xs={7}>
                      {entry.ozow.paymentLink === "true" ? "Yes" : "No"}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={5} className="fw-bold">
                      Online Payment:
                    </Col>
                    <Col xs={7}>
                      {entry.ozow.onlinePayment === "true" ? "Yes" : "No"}
                    </Col>
                  </Row>
                </Col>

                <Col md={4}>
                  <h6 className="text-primary mb-3">Yoco Web POS</h6>
                    {(yocoDevices[entry.practiceNumber]?.length ?? 0) === 0 ? (
                      <div className="text-muted small">No devices yet</div>
                    ) : (
                      <ul className="small mb-0">
                        {yocoDevices[entry.practiceNumber].map((d) => (
                          <li key={d.id}>
                            <code>{d.name}</code> —{" "}
                            <span className="text-muted">{d.id}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      })}
      {modalShow && selectedBanking && (
        <EditBankingModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          bankingDetail={selectedBanking}
          onBankingUpdate={() => {
            setModalShow(false);
            setSelectedBanking(null);
            // refresh the banking data
            const token = sessionStorage.getItem("user");
            if (token) {
              const decoded = jwtDecode(token);
              const load = async () => {
                const userPractices = await getUserPracticeById(decoded._id);
                const practiceNumbers = userPractices.map(
                  (p) => p.practiceNumber
                );
                const result = await getBankingDetailsByPracticeNumbers(
                  practiceNumbers
                );
                setBankingData(result);
              };
              load();
            }
          }}
        />
      )}
    </Container>
  );
}
